/**
 * P-CDM Frontend Application
 * 支援三種響應式解析度:
 * - Desktop: >1200px
 * - Tablet: 768-1200px
 * - Mobile: <768px
 * 支援中英雙語系統 (zh-TW / en)
 */
import { useEffect, useState } from 'react';
import { RouterProvider } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ConfigProvider, theme, App as AntApp } from 'antd';
import type { Locale } from 'antd/es/locale';
import zhTW from 'antd/locale/zh_TW';
import enUS from 'antd/locale/en_US';
import { useTranslation } from 'react-i18next';
import { router } from './routes';
import './i18n'; // 初始化 i18n
import './App.css';

// 創建 React Query 客戶端
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      refetchOnMount: 'always', // 總是重新載入最新資料
      retry: 1,
      staleTime: 0, // 資料立即過期,確保獲取最新資料
    },
  },
});

// Ant Design 主題配置 - 深色模式
const antdTheme = {
  algorithm: theme.darkAlgorithm,
  token: {
    colorPrimary: '#1890ff',
    borderRadius: 6,
    fontSize: 14,
    colorBgContainer: '#051326',
    colorBgLayout: '#051326',
  },
};

// Ant Design locale 映射
const antdLocaleMap: Record<string, Locale> = {
  'zh-TW': zhTW,
  en: enUS,
};

function App() {
  const { i18n } = useTranslation();
  const [antdLocale, setAntdLocale] = useState<Locale>(zhTW);

  // 監聽語言變化，同步更新 Ant Design locale
  useEffect(() => {
    const locale = antdLocaleMap[i18n.language] || zhTW;
    setAntdLocale(locale);
  }, [i18n.language]);

  return (
    <ConfigProvider locale={antdLocale} theme={antdTheme}>
      <QueryClientProvider client={queryClient}>
        <AntApp>
          <RouterProvider router={router} />
        </AntApp>
      </QueryClientProvider>
    </ConfigProvider>
  );
}

export default App;
